import React from 'react';

import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { EditWarehouse } from '@features/editWarehouse';
import { EquipmentInventoryModal } from '@features/equipmentInventoryModal';
import { Icon, Input, Popup, UiButton, UiText, UITooltip } from '@shared/ui';

import { INVENTORY_CARD_HEIGHT } from '../../lib/constants';
import { useWarehouseColumn } from '../../model/useWarehouseColumn';
import { SortableInventoryCard } from '../SortableInventoryCard';
import { TooltipWarehouseColumnInfo } from '../WarehouseColumnInfo';
import cls from './WarehouseColumn.module.css';

import type { Warehouse } from '@entities/equipment';

interface WarehouseColumnProps {
    warehouse: Warehouse;
    warehouses: Warehouse[];
    hasFullAccess: boolean;
    currencySign: string;
    inventoryItems?: Warehouse['items'];
    isFiltering?: boolean;
    activeInventoryGuid?: string | null;
    inventoryDropPreviewIndex?: number | null;
    isInventoryDragDisabled?: boolean;
    columnDragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const WarehouseColumn = ({
    warehouse,
    warehouses,
    hasFullAccess,
    currencySign,
    inventoryItems,
    isFiltering = false,
    activeInventoryGuid,
    inventoryDropPreviewIndex,
    isInventoryDragDisabled = false,
    columnDragHandleProps,
}: WarehouseColumnProps) => {
    const {
        t,
        isRenameMode,
        warehouseTitle,
        setWarehouseTitle,
        displayedInventoryItems,
        hasInventoryItems,
        inventorySortableIds,
        setInventoryDropzoneRef,
        setCardsContainerNode,
        virtualItems,
        virtualTotalSize,
        availableWarehouses,
        openedInventoryGuid,
        isInventoryModalOpen,
        inventoryGuidForDelete,
        inventoryModalMode,
        isCreateInventoryPending,
        isDeleteInventoryPending,
        canRenameWarehouse,
        canMoveWarehouseItems,
        startRename,
        finishRename,
        createInventory,
        copyInventory,
        openInventoryModal,
        closeInventoryModal,
        clearClosedInventoryModal,
        moveInventory,
        changeInventoryStatus,
        deleteInventory,
        confirmDeleteInventory,
        closeDeleteInventoryPopup,
    } = useWarehouseColumn({
        warehouse,
        warehouses,
        inventoryItems,
        activeInventoryGuid,
        hasFullAccess,
    });

    return (
        <div
            ref={!hasInventoryItems ? setInventoryDropzoneRef : undefined}
            className={cls.WarehouseColumn}
        >
            <div className={cls.WarehouseColumnHeader} {...columnDragHandleProps}>
                {warehouse.isAttachedToProject && (
                    <div className={cls.homeIcon}>
                        <Icon iconName="house" />
                    </div>
                )}
                {isRenameMode ? (
                    <Input
                        autoFocus
                        maxLength={255}
                        value={warehouseTitle}
                        onChange={(event) => setWarehouseTitle(event.target.value)}
                        onBlur={() => {
                            void finishRename();
                        }}
                        onFocus={(event) => event.target.select()}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                void finishRename();
                            }
                        }}
                        classes={[cls.renameInputWrapper]}
                        inputClassName={cls.renameInput}
                    />
                ) : (
                    <>
                        <UiText
                            weight="medium"
                            variant="subPlain"
                            tag="h2"
                            className={cls.WarehouseColumnHeaderTitle}
                        >
                            {warehouse.title}
                        </UiText>
                        {!isFiltering && (
                            <UITooltip
                                placement="top"
                                classes={cls.iconWrapper}
                                label={
                                    <TooltipWarehouseColumnInfo
                                        totalInfo={warehouse.totalInfo}
                                    />
                                }
                            >
                                <Icon iconName="info" />
                            </UITooltip>
                        )}
                        <EditWarehouse
                            warehouseId={warehouse.id}
                            warehouseTitle={warehouse.title}
                            itemsCount={warehouse.items.length}
                            inventoryItems={displayedInventoryItems}
                            hasFilterValue={isFiltering}
                            warehouses={availableWarehouses}
                            canRename={canRenameWarehouse}
                            canMove={canMoveWarehouseItems}
                            onRenameClick={startRename}
                        />
                    </>
                )}
            </div>
            {hasInventoryItems && (
                <div ref={setCardsContainerNode} className={cls.cardWrapper}>
                    <SortableContext
                        items={inventorySortableIds}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className={cls.virtualItems} style={{ height: virtualTotalSize }}>
                            {typeof inventoryDropPreviewIndex === 'number' && (
                                <div
                                    className={cls.inventoryDropPlaceholder}
                                    style={{
                                        top: inventoryDropPreviewIndex * INVENTORY_CARD_HEIGHT,
                                    }}
                                />
                            )}
                            {virtualItems.map((virtualItem) => {
                                const warehouseItem = displayedInventoryItems[virtualItem.index];

                                if (!warehouseItem) {
                                    return null;
                                }

                                return (
                                    <SortableInventoryCard
                                        key={virtualItem.key}
                                        warehouseId={warehouse.id}
                                        warehouseItem={warehouseItem}
                                        virtualStart={virtualItem.start}
                                        warehouses={availableWarehouses}
                                        hasFullAccess={hasFullAccess}
                                        canMove={availableWarehouses.length > 0}
                                        onClick={() => openInventoryModal(warehouseItem.guid)}
                                        onCopyClick={(event) => {
                                            event.stopPropagation();
                                            copyInventory(warehouseItem.guid);
                                        }}
                                        onMoveClick={(toWarehouse, closeDropdown) =>
                                            moveInventory(
                                                warehouseItem.guid,
                                                toWarehouse,
                                                closeDropdown
                                            )
                                        }
                                        onStatusClick={changeInventoryStatus(warehouseItem.guid)}
                                        onDeleteClick={deleteInventory(warehouseItem.guid)}
                                        isActiveDragItem={
                                            warehouseItem.guid === activeInventoryGuid
                                        }
                                        isDragDisabled={isInventoryDragDisabled}
                                    />
                                );
                            })}
                        </div>
                    </SortableContext>
                </div>
            )}
            {!isFiltering && hasFullAccess && (
                <UiButton
                    data-id="create-inventory-button"
                    btnColor="secondary"
                    className={cls.addWarehouse}
                    startIcon={<Icon iconName="plus" />}
                    disabled={isCreateInventoryPending}
                    onClick={createInventory}
                />
            )}
            <EquipmentInventoryModal
                isOpen={isInventoryModalOpen}
                mode={inventoryModalMode}
                inventoryGuid={openedInventoryGuid}
                warehouseId={warehouse.id}
                warehouseTitle={warehouse.title}
                hasFullAccess={hasFullAccess}
                currencySign={currencySign}
                onClose={closeInventoryModal}
                onAfterClose={clearClosedInventoryModal}
            />
            <Popup
                isOpen={Boolean(inventoryGuidForDelete)}
                type="delete"
                contentText={t('Inventory.deleteInventory.text')}
                deleteButtonText={t('Inventory.deleteInventory.deleteBtn.text')}
                cancelButtonText={t('Inventory.deleteInventory.cancelBtn.text')}
                onHidePopup={closeDeleteInventoryPopup}
                btnActionCb={confirmDeleteInventory}
                isPending={isDeleteInventoryPending}
            />
        </div>
    );
};
